import { ApiErrorSchema, type Pagination } from '@taskall/contracts';
import { auth } from './firebase';

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Erros de campo retornados pelo backend (VALIDATION_ERROR / conflitos). */
  get fieldErrors(): Record<string, string> {
    const fields = this.details?.fields;
    return fields && typeof fields === 'object' ? (fields as Record<string, string>) : {};
  }
}

/** Somente valores primitivos viram parâmetros de query. */
type Query = Record<string, unknown>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Token forçado (ex.: logo após o login). */
  token?: string;
}

function buildUrl(path: string, query?: Query): string {
  const url = `${BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function authHeader(token?: string): Promise<Record<string, string>> {
  const idToken = token ?? (await auth.currentUser?.getIdToken());
  return idToken ? { Authorization: `Bearer ${idToken}` } : {};
}

async function toApiError(res: Response): Promise<ApiError> {
  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    // corpo não-JSON
  }
  const parsed = ApiErrorSchema.safeParse(payload);
  if (parsed.success) {
    const e = parsed.data;
    return new ApiError(res.status, e.error.code, e.error.message, e.error.details, e.requestId);
  }
  return new ApiError(res.status, 'HTTP_ERROR', res.status >= 500 ? 'Serviço indisponível. Tente novamente.' : 'Falha na requisição.');
}

async function raw(path: string, opts: RequestOptions = {}): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', ...(await authHeader(opts.token)), ...opts.headers };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), { method: opts.method ?? 'GET', headers, body, signal: opts.signal });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new ApiError(0, 'NETWORK_ERROR', 'Sem conexão com o servidor. Verifique sua internet.');
  }
  if (!res.ok) throw await toApiError(res);
  return res;
}

/** Requisição JSON com envelope { data }. */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await raw(path, opts);
  const json = (await res.json()) as { data: T };
  return json.data;
}

/** Requisição paginada { data, pagination }. */
export async function apiPage<T>(path: string, opts: RequestOptions = {}): Promise<{ data: T[]; pagination: Pagination }> {
  const res = await raw(path, opts);
  return (await res.json()) as { data: T[]; pagination: Pagination };
}

/** Download autenticado (o arquivo nunca é público). */
export async function apiDownload(path: string): Promise<{ blob: Blob; filename: string }> {
  const res = await raw(path, { headers: { Accept: '*/*' } });
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? 'arquivo';
  return { blob: await res.blob(), filename };
}

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Erro inesperado.';
}
